import React, { useEffect, useState } from 'react';
import { categoryService, gameService } from '../services/api';
import ImageUpload from '../components/ImageUpload';
import CategoryManager from '../components/CategoryManager';

const EMPTY = { name: '', icon_url: '', category: '', sort_order: 0, machine_ids: [], machine_configs: {}, machine_statuses: {} };

export default function Games() {
  const [games, setGames] = useState([]);
  const [machines, setMachines] = useState([]);
  const [modal, setModal] = useState(null); // { game? }
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [testMsg, setTestMsg] = useState({});
  const [pathModal, setPathModal] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryModal, setCategoryModal] = useState(false);

  const load = async () => {
    const [gamesRes, machinesRes, categoriesRes] = await Promise.all([
      gameService.getAll(), gameService.getMachines(), categoryService.getAll('game'),
    ]);
    setGames(gamesRes.data);
    setMachines(machinesRes.data);
    setCategories(categoriesRes.data);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...EMPTY, machine_ids: [], machine_configs: {}, machine_statuses: {} }); setTestMsg({}); setPathModal(null); setModal({}); };
  const openEdit = (game) => {
    const savedCategory = String(game.category || '').trim();
    const matchedCategory = categories.find(category =>
      category.name.trim().toLocaleLowerCase('pt-BR') === savedCategory.toLocaleLowerCase('pt-BR')
    );
    setForm({
      name: game.name,
      icon_url: game.icon_url || '', category: matchedCategory?.name || savedCategory,
      sort_order: game.sort_order || 0,
      machine_ids: game.machine_ids || [],
      machine_configs: game.machine_configs || {},
      machine_statuses: game.machine_statuses || {},
    });
    setTestMsg({});
    setPathModal(null);
    setModal({ game });
  };

  const openPathModal = (machine) => {
    const config = form.machine_configs[machine.id] || {};
    setPathModal({
      machine,
      exe_path: config.exe_path || '',
      args: config.args || '',
      run_as_admin: !!config.run_as_admin,
    });
  };

  const savePathModal = async (test = false) => {
    const exePath = pathModal?.exe_path?.trim() || '';
    const machine = pathModal.machine;
    const nextForm = {
      ...form,
      machine_configs: {
        ...form.machine_configs,
        [machine.id]: { exe_path: exePath, args: pathModal.args?.trim() || '', run_as_admin: !!pathModal.run_as_admin },
      },
    };
    setForm(nextForm);
    if (test && modal.game) {
      setTestMsg(current => ({ ...current, [machine.id]: 'Enviando teste…' }));
      try {
        await gameService.update(modal.game.id, nextForm);
        await gameService.testOnMachine(modal.game.id, machine.id);
        setTestMsg(current => ({ ...current, [machine.id]: 'Caminho salvo; teste enviado ao equipamento.' }));
      } catch (err) {
        setTestMsg(current => ({ ...current, [machine.id]: err.response?.data?.error || err.message }));
        return;
      }
    }
    setPathModal(null);
  };

  const handleSave = async () => {
    if (!form.name) {
      alert('Nome é obrigatório');
      return;
    }
    setLoading(true);
    try {
      if (modal.game) await gameService.update(modal.game.id, form);
      else await gameService.create(form);
      setModal(null);
      load();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (game) => {
    if (!window.confirm(`Remover "${game.name}" da lista de jogos?`)) return;
    await gameService.delete(game.id);
    load();
  };

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Jogos</h1>
          <p style={styles.sub}>
            {games.length} jogo(s) — aparecem somente nos equipamentos selecionados
          </p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.btnSecondary} onClick={() => setCategoryModal(true)}>Categorias</button>
          <button style={styles.btnPrimary} onClick={openNew}>+ Novo Jogo</button>
        </div>
      </div>

      <div style={styles.hint}>
        💡 O caminho é opcional no cadastro. Quando necessário, use a engrenagem para configurar
        o executável ou link Steam separadamente em cada equipamento.
      </div>

      <div style={styles.ranking}>
        <div style={styles.rankingTitle}>🏆 Mais jogados — últimos 30 dias</div>
        <div style={styles.rankingGrid}>
          {[...games].sort((a,b) => Number(b.play_count)-Number(a.play_count)).slice(0,5).map((g,i) => (
            <div key={g.id} style={styles.rankCard}>
              <span style={styles.rankPos}>{i+1}º</span>
              {g.icon_url ? <img src={g.icon_url} alt="" style={styles.rankThumb}/> : <span style={styles.rankFallback}>🎮</span>}
              <div style={{minWidth:0}}><strong style={styles.rankName}>{g.name}</strong><small style={styles.rankStats}>{g.play_count || 0} jogadas · {g.unique_players || 0} jogadores</small></div>
            </div>
          ))}
        </div>
      </div>

      <div className="touch-scroll-x" style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>#</th>
              <th style={styles.th}>Capa</th>
              <th style={styles.th}>Nome</th>
              <th style={styles.th}>Categoria</th>
              <th style={styles.th}>Equipamentos</th>
              <th style={styles.th}>Jogadas (30 dias)</th>
              <th style={styles.th}>Cliques no catálogo</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {games.map(g => (
              <tr key={g.id} style={styles.tr}>
                <td style={styles.td}>{g.sort_order}</td>
                <td style={styles.td}>
                  {g.icon_url
                    ? <img src={g.icon_url} alt={g.name} style={styles.gameThumb}
                        onError={e => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }} />
                    : null}
                  <div style={{ ...styles.gameThumbFallback, display: g.icon_url ? 'none' : 'flex' }}>🎮</div>
                </td>
                <td style={{ ...styles.td, color: '#fff', fontWeight: 600 }}>{g.name}</td>
                <td style={styles.td}>{g.category || '—'}</td>
                <td style={styles.td}>{g.machine_ids?.length || 0} selecionado(s)</td>
                <td style={styles.td}>{g.play_count || 0}</td>
                <td style={styles.td}><strong style={{color:'#a3e635'}}>{g.click_count || 0}</strong><small style={{display:'block',color:'#6b7280'}}>{g.click_count_30d || 0} nos últimos 30 dias</small></td>
                <td style={{ ...styles.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button style={styles.btnSm} onClick={() => openEdit(g)}>Editar</button>
                  <button style={styles.btnSmDanger} onClick={() => handleDelete(g)}>Remover</button>
                </td>
              </tr>
            ))}
            {!games.length && (
              <tr><td style={styles.td} colSpan={8}>Nenhum jogo cadastrado ainda.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>{modal.game ? 'Editar Jogo' : 'Novo Jogo'}</h2>

            <div style={styles.modalGrid}>
              <Field label="Nome *" full>
                <input style={styles.input} value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Categoria">
                <select style={styles.input} value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}>
                  <option value="">Sem categoria</option>
                  {form.category && !categories.some(category => category.name === form.category) &&
                    <option value={form.category}>{form.category}</option>}
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>{category.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Ordem">
                <input type="number" style={styles.input} value={form.sort_order}
                  onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
              </Field>
            </div>
            <div style={{ marginBottom: 14 }}>
              <ImageUpload label="Ícone/capa do jogo (opcional — senão usa o ícone do .exe)"
                value={form.icon_url} onChange={(url) => setForm({ ...form, icon_url: url })} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={styles.label}>Disponível em quais equipamentos?</label>
              <div style={styles.machineList}>
                {machines.map(machine => {
                  const checked = form.machine_ids.includes(machine.id);
                  const machineStatus = form.machine_statuses[machine.id];
                  const compact = compactStatus(machineStatus, form.machine_configs[machine.id]);
                  return (
                    <div key={machine.id} style={{ ...styles.machineOption, ...(checked ? styles.machineOptionOn : {}) }}>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, cursor: 'pointer' }}>
                      <input type="checkbox" checked={checked} onChange={() => setForm({
                        ...form,
                        machine_ids: checked
                          ? form.machine_ids.filter(id => id !== machine.id)
                          : [...form.machine_ids, machine.id],
                        machine_configs: checked ? form.machine_configs : {
                          ...form.machine_configs,
                          [machine.id]: form.machine_configs[machine.id] || { exe_path: '', args: '' },
                        },
                      })} />
                      <span style={styles.machineName}>{deviceIcon(machine.device_type)} {machine.name}</span>
                      {checked && <span style={styles.compactStatus} title={testMsg[machine.id] || statusText(machineStatus)}>
                        <span style={{ ...styles.statusDot, background: compact.color, boxShadow: `0 0 6px ${compact.color}` }} />
                        <span style={{ color: compact.color }}>{compact.label}</span>
                      </span>}
                      </label>
                      {checked && <button type="button" title="Configurar caminho neste equipamento"
                        style={styles.gearBtn} onClick={() => openPathModal(machine)}>⚙</button>}
                    </div>
                  );
                })}
                {!machines.length && <span style={{ color: '#6b7280', fontSize: 13 }}>Cadastre um equipamento primeiro.</span>}
              </div>
            </div>

            <div style={styles.modalActions}>
              <button style={styles.btnSecondary} onClick={() => setModal(null)}>Cancelar</button>
              <button style={styles.btnPrimary} onClick={handleSave} disabled={loading}>
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pathModal && (
        <div style={styles.pathOverlay}>
          <div style={styles.pathModalCard}>
            <div style={styles.pathModalHeader}>
              <div>
                <h2 style={styles.modalTitle}>Configurar caminho do jogo</h2>
                <span style={styles.machineBadge}>{deviceIcon(pathModal.machine.device_type)} {pathModal.machine.name}</span>
              </div>
              <button type="button" style={styles.closeBtn} onClick={() => setPathModal(null)}>×</button>
            </div>
            <p style={styles.pathHint}>Esta instrução vale somente para este equipamento. Aceita caminho, comando completo, Java/JAR, BAT/CMD, atalho ou protocolo como Steam.</p>
            <Field label="Comando, caminho ou protocolo (opcional)">
              <input autoFocus style={styles.input} placeholder={'Ex.: "C:\\Java\\javaw.exe" -jar "C:\\Games\\launcher.jar"'}
                value={pathModal.exe_path}
                onChange={e => setPathModal({ ...pathModal, exe_path: e.target.value })} />
            </Field>
            <Field label="Argumentos adicionais (opcional)">
              <input style={styles.input} placeholder="Ex.: -fullscreen"
                value={pathModal.args}
                onChange={e => setPathModal({ ...pathModal, args: e.target.value })} />
            </Field>
            <label style={styles.adminToggle}>
              <input type="checkbox" checked={!!pathModal.run_as_admin}
                onChange={e => setPathModal({ ...pathModal, run_as_admin: e.target.checked })} />
              <span><strong>Executar como administrador</strong><small>Na primeira abertura, o técnico confirma o UAC. Depois este comando abre elevado sem liberar o restante do Windows.</small></span>
            </label>
            <div style={styles.technicalInfo}>
              <div style={styles.technicalRow}>
                <span>Status</span>
                <strong style={{ color: statusColor(form.machine_statuses[pathModal.machine.id]?.status) }}>
                  {testMsg[pathModal.machine.id] || statusText(form.machine_statuses[pathModal.machine.id])}
                </strong>
              </div>
              <div style={styles.technicalRow}>
                <span>Última validação</span>
                <strong>{formatCheckedAt(form.machine_statuses[pathModal.machine.id]?.checked_at)}</strong>
              </div>
              <div style={styles.technicalRow}>
                <span>Equipamento</span>
                <strong>{pathModal.machine.name} · {pathModal.machine.device_type?.toUpperCase()}</strong>
              </div>
            </div>
            <div style={styles.modalActions}>
              <button type="button" style={styles.btnSecondary} onClick={() => setPathModal(null)}>Cancelar</button>
              {modal?.game && <button type="button" style={styles.testBtn} onClick={() => savePathModal(true)}>Salvar e testar</button>}
              <button type="button" style={styles.btnPrimary} onClick={() => savePathModal(false)}>Salvar caminho</button>
            </div>
          </div>
        </div>
      )}
      {categoryModal && (
        <CategoryManager kind="game" title="Categorias dos Jogos"
          onClose={() => setCategoryModal(false)} onChange={load} />
      )}
    </div>
  );
}

function deviceIcon(type) {
  return { pc: '🖥️', ps5: '🎮', cockpit: '🏎️', metaquest: '🥽' }[type] || '🖥️';
}

function statusColor(status) {
  return ['valid', 'launched'].includes(status) ? '#22c55e' : status ? '#f87171' : '#6b7280';
}

function statusText(status) {
  if (!status) return 'Aguardando validação do ArenaClient';
  return { valid: '✓ Caminho validado', launched: '✓ Jogo abriu', missing: '✕ Arquivo não encontrado', invalid: '✕ Caminho inválido', launch_error: '✕ Erro ao abrir' }[status.status] || status.message;
}

function compactStatus(status, config) {
  if (['valid', 'launched'].includes(status?.status)) return { label: 'OK', color: '#22c55e' };
  if (status?.status) return { label: 'ERRO', color: '#ef4444' };
  if (config?.exe_path) return { label: 'PENDENTE', color: '#f59e0b' };
  return { label: 'OPCIONAL', color: '#6b7280' };
}

function formatCheckedAt(value) {
  if (!value) return 'Ainda não validado';
  return new Date(value).toLocaleString('pt-BR');
}

function Field({ label, children, flex, full }) {
  return (
    <div style={{ marginBottom: 14, ...(flex ? { flex: 1 } : {}), ...(full ? styles.fullSpan : {}) }}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
  headerActions: { display: 'flex', alignItems: 'center', gap: 10 },
  title: { color: '#fff', fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { color: '#6b7280', fontSize: 14, margin: '4px 0 0' },
  btnPrimary: { padding: '10px 18px', background: 'var(--accent)', color: '#0a0a0f', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  hint: { background: '#14141f', border: '1px solid #262636', borderRadius: 8, padding: '12px 16px', color: '#9ca3af', fontSize: 13, marginBottom: 20 },
  ranking: { background:'#14141f',border:'1px solid #262636',borderRadius:12,padding:16,marginBottom:18 },
  rankingTitle:{color:'#e5e7eb',fontWeight:700,marginBottom:12},rankingGrid:{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(190px,1fr))',gap:10},
  rankCard:{display:'flex',alignItems:'center',gap:10,background:'#0e0e16',border:'1px solid #262636',borderRadius:9,padding:10},rankPos:{color:'var(--accent)',fontWeight:800},rankThumb:{width:42,height:42,objectFit:'cover',borderRadius:7},rankFallback:{fontSize:25},rankName:{display:'block',color:'#fff',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},rankStats:{display:'block',color:'#6b7280',marginTop:3},
  code: { background: '#262636', padding: '2px 6px', borderRadius: 4, color: '#84cc16' },
  tableWrap: { background: '#14141f', border: '1px solid #262636', borderRadius: 12 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '12px 16px', color: '#6b7280', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #262636' },
  tr: { borderBottom: '1px solid #262636' },
  td: { padding: '12px 16px', color: '#9ca3af', fontSize: 14 },
  gameThumb: { width: 58, height: 42, display: 'block', objectFit: 'cover', borderRadius: 7, border: '1px solid #34344e', background: '#0e0e16' },
  gameThumbFallback: { width: 58, height: 42, alignItems: 'center', justifyContent: 'center', borderRadius: 7, border: '1px solid #34344e', background: '#0e0e16', fontSize: 20 },
  btnSm: { padding: '6px 12px', background: 'transparent', border: '1px solid #374151', borderRadius: 6, color: '#9ca3af', cursor: 'pointer', fontSize: 13, marginLeft: 8 },
  btnSmDanger: { padding: '6px 12px', background: 'transparent', border: '1px solid #7f1d1d', borderRadius: 6, color: '#f87171', cursor: 'pointer', fontSize: 13, marginLeft: 8 },
  overlay: { position: 'fixed', inset: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 50, padding: '24px 12px' },
  modal: { background: '#14141f', border: '1px solid #262636', borderRadius: 16, padding: 28, width: 760, maxWidth: 'calc(100vw - 28px)', maxHeight: 'calc(100dvh - 48px)', overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box' },
  modalTitle: { color: '#fff', fontSize: 20, fontWeight: 700, margin: '0 0 20px' },
  modalGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 12px' },
  fullSpan: { gridColumn: '1 / -1' },
  label: { display: 'block', color: '#9ca3af', fontSize: 13, fontWeight: 500, marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', background: '#262636', border: '1px solid #374151', borderRadius: 8, color: '#fff', fontSize: 14, boxSizing: 'border-box' },
  modalActions: { display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 },
  btnSecondary: { padding: '10px 18px', background: 'transparent', border: '1px solid #374151', borderRadius: 8, color: '#9ca3af', cursor: 'pointer', fontSize: 14 },
  machineList: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: 10, background: '#0e0e16', border: '1px solid #262636', borderRadius: 8, maxHeight: 180, overflowY: 'auto' },
  machineOption: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 5, alignItems: 'center', padding: '7px 8px', border: '1px solid #262636', borderRadius: 8, color: '#9ca3af', fontSize: 12 },
  machineOptionOn: { borderColor: 'var(--accent)', color: '#fff', background: 'rgba(132,204,22,0.08)' },
  machineName: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  gearBtn: { width: 28, height: 27, padding: 0, background: '#262636', border: '1px solid var(--accent)', borderRadius: 6, color: 'var(--accent)', cursor: 'pointer', fontSize: 14 },
  compactStatus: { display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, fontSize: 9, fontWeight: 800, letterSpacing: 0.3 },
  statusDot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },
  pathOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 70 },
  pathModalCard: { background: '#14141f', border: '1px solid #34344e', borderRadius: 16, padding: 26, width: 540, maxWidth: '92vw', boxShadow: '0 24px 80px rgba(0,0,0,0.55)' },
  pathModalHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 },
  machineBadge: { display: 'inline-block', background: 'rgba(132,204,22,0.1)', border: '1px solid rgba(132,204,22,0.35)', borderRadius: 999, color: 'var(--accent)', fontSize: 12, fontWeight: 700, padding: '5px 10px' },
  closeBtn: { width: 34, height: 34, background: '#262636', border: '1px solid #374151', borderRadius: 8, color: '#9ca3af', cursor: 'pointer', fontSize: 22, lineHeight: 1 },
  pathHint: { color: '#9ca3af', fontSize: 13, lineHeight: 1.5, margin: '0 0 18px', padding: '10px 12px', background: '#0e0e16', borderRadius: 8 },
  technicalInfo: { display: 'flex', flexDirection: 'column', gap: 8, padding: 12, background: '#0e0e16', border: '1px solid #262636', borderRadius: 8, fontSize: 12 },
  technicalRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, color: '#6b7280' },
  testBtn: { padding: '10px 16px', background: 'transparent', border: '1px solid var(--accent)', borderRadius: 8, color: 'var(--accent)', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  adminToggle: { display:'flex',alignItems:'flex-start',gap:10,padding:12,marginBottom:14,background:'#251b0b',border:'1px solid #92400e',borderRadius:9,color:'#fef3c7',cursor:'pointer',fontSize:12 },
};
